/**
 * Content Studio — AI generation session types (FE contract for the Task 17
 * routes, consumed by Task 20's studio page).
 *
 * NOTE (Task 20): the FE lane owns this file per the plan ("create it
 * yourself per plan Task 17" — the BE lane had not created it). The shapes
 * below are VERIFIED against the landed BE sources:
 *   apps/api/src/modules/content-studio/dto/session.dto.ts
 *   apps/api/src/modules/content-studio/controllers/studio-ai.controller.ts
 * These are OWN types — never import/widen the phishing or announcement
 * ai-studio session types (spec §7: their columns and stale-watchdog
 * semantics do not fit content decks).
 *
 * Route contract (all under `/v1/content-studio/ai`):
 *   POST `sessions`             — create (CreateSessionDto); status
 *                                 'In Progress'; initial job-progress state
 *                                 written BEFORE enqueue
 *   GET  `sessions`             — history list (paginated envelope of the
 *                                 SUMMARY projection — no payload members)
 *   GET  `sessions/:id`         — polling fallback (full SessionResponseDto)
 *   GET  `sessions/:id/stream`  — SSE relay, 15s heartbeat
 *   POST `sessions/:id/cancel`  — flips status to 'Cancelled'
 *   POST `sessions/:id/save`    — deckJson → content_decks row →
 *                                 { deckId, deckStatus, sessionId }
 *   POST `sessions/:id/resume`  — Continue a Failed / stalled run in the
 *                                 SAME session → the detail row; 409
 *                                 `generation_still_running` |
 *                                 `generation_not_resumable`
 *
 * SSE wire contract (`GET sessions/:id/stream?cursor=<seq>`):
 *   event: progress   TWO payload flavors on one event name —
 *                     (a) seq-numbered checklist entries from the session
 *                         row (SessionProgressEntry), replayed above the
 *                         `?cursor=` on (re)connect;
 *                     (b) the Redis job-progress snapshot
 *                         ({jobId,status,progress,message,…} — NO seq).
 *   event: status     data: { status: GenerationSessionStatus }
 *   event: heartbeat  data: { at: string }            (15s server cadence)
 *   event: draft      data: the worker's live draft, relayed RAW from Redis
 *                     (`{ v: 1, rev, phase, pictureStyle, title, cards[] }`,
 *                     lib/content-studio/live-draft.ts) — sent when it
 *                     changes; `rev` restarts at 1 on every run
 *   event: deck-drafted data: { sessionId, deckId } — the Draft deck row
 *                     exists (once per connection, before `done`)
 *   event: done       data: { sessionId, status, deckId } — terminal
 *   event: error      data: { sessionId, code } — STREAM_TIMEOUT is a
 *                     benign server-side stream recycle (reconnect);
 *                     SESSION_GONE and anything else is terminal.
 * The FE parser is deliberately tolerant: unknown event names only reset
 * the heartbeat timer, and progress data is routed on "has a numeric seq".
 */

// ── Status ─────────────────────────────────────────────────────────────────

export const GENERATION_SESSION_STATUSES = [
  'In Progress',
  'Needs Review',
  'Completed',
  'Failed',
  'Cancelled',
] as const
export type GenerationSessionStatus =
  (typeof GENERATION_SESSION_STATUSES)[number]

/** Every status except 'In Progress' — streaming/polling stops here. */
export const TERMINAL_SESSION_STATUSES = [
  'Needs Review',
  'Completed',
  'Failed',
  'Cancelled',
] as const satisfies readonly GenerationSessionStatus[]

export function isGenerationSessionStatus(
  value: unknown,
): value is GenerationSessionStatus {
  return (
    typeof value === 'string' &&
    (GENERATION_SESSION_STATUSES as readonly string[]).includes(value)
  )
}

export function isTerminalSessionStatus(
  status: GenerationSessionStatus,
): boolean {
  return status !== 'In Progress'
}

// ── Progress ───────────────────────────────────────────────────────────────

/**
 * Stage ids the Task 19 processor writes. Kept as `string` on the wire type
 * (KNOWN_GENERATION_STAGES is for checklist ORDERING) so a new BE stage
 * renders as generic activity instead of crashing the checklist.
 */
export const KNOWN_GENERATION_STAGES = [
  'queued',
  'outline',
  'cards',
  'repair',
  'assembly',
  'checker',
] as const
export type KnownGenerationStage = (typeof KNOWN_GENERATION_STAGES)[number]

export interface SessionProgressEntry {
  /** Monotonic cursor — reconnects resume from the highest applied seq. */
  seq: number
  stage: string
  message: string
  /** ISO timestamp. */
  at: string
  batchIndex?: number
  totalBatches?: number
  cardsDone?: number
  cardsTotal?: number
}

/** The session row's `progress` jsonb (initial state written before enqueue). */
export interface SessionProgressState {
  cursor: number
  entries: SessionProgressEntry[]
  /** Persisted worker checkpoint used by the GET-polling fallback. */
  stage: string | null
}

// ── Checker ────────────────────────────────────────────────────────────────

/** Fixed rubric booleans from `content-deck-checker-v1` (spec §7). */
export interface SessionCheckerResult {
  accuracyOk: boolean
  toneOk: boolean
  safetyOk: boolean
  notes: string[]
}

// ── Job-progress snapshot (the seq-less `event: progress` flavor) ──────────

/**
 * The worker's Redis job-progress state, relayed over the same `progress`
 * SSE event as the checklist entries but WITHOUT a `seq`. Carries the
 * coarse percent + message ("Queued for generation", "Batch 2/5") that
 * exists before any row entries do.
 */
export interface SessionJobSnapshot {
  status: string | null
  /** Product-specific worker stage (for example cards, assembly, checker). */
  stage: string | null
  /** 0–100 (the wire member is named `progress`). */
  percent: number | null
  message: string | null
}

// ── Session rows ───────────────────────────────────────────────────────────

/** History-list projection (SessionSummaryDto) — NO payload members. */
export interface GenerationSessionSummary {
  id: string
  prompt: string
  status: GenerationSessionStatus
  /** Stamped by POST :id/save once the deck row exists. */
  deckId: string | null
  errorCode: string | null
  errorMessage: string | null
  lastChunkAt: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Every generation parameter a run was started with, RESOLVED by the API
 * (brief choice, else the workspace default, else the platform value) —
 * `StoredGenerationParams` in apps/api sessions.service.ts. Continue reuses
 * it server-side; Start over sends it back in a new create body.
 */
export interface SessionGenerationParams {
  audience: string | null
  locale: string | null
  targetCardCount: number | null
  paletteKey: string | null
  backdrop: string | null
  quality: GenerationQuality
  pictureStyle: PictureStyle
}

/** Detail projection (SessionResponseDto) — summary + pipeline payloads. */
export interface GenerationSession extends GenerationSessionSummary {
  outlineJson: Record<string, unknown> | null
  /** Assembled deck content — present once generation completed. */
  deckJson: Record<string, unknown> | null
  checkerJson: SessionCheckerResult | null
  progress: SessionProgressState
  /** null for a run started before the parameters were stored. */
  generationParams: SessionGenerationParams | null
}

// ── Requests ───────────────────────────────────────────────────────────────

/**
 * POST /content-studio/ai/sessions body (CreateSessionDto mirror): brief
 * (8–4000 chars) + optional authoring hints. The API resolves the hints
 * against the workspace defaults and stores them on the session row
 * (`generationParams`), so Continue re-runs with exactly what the run was
 * started with; they also travel in the queue job for the whole-deck call.
 */
export interface CreateSessionInput {
  prompt: string
  audience?: string
  locale?: string
  /** AI-generated deck target, 14–24 cards. */
  targetCardCount?: number
  paletteKey?: string
  /**
   * 'supercraft' adds one senior-editor revision pass (API: one extra charge,
   * only when its revised deck is delivered). Omitted means standard.
   */
  quality?: GenerationQuality
  /** The deck's picture style; omitted means the workspace default. */
  pictureStyle?: PictureStyle
}

/** CreateStudioSessionDto.quality mirror (apps/api dto/session.dto.ts). */
export const GENERATION_QUALITIES = ['standard', 'supercraft'] as const
export type GenerationQuality = (typeof GENERATION_QUALITIES)[number]

/**
 * A generated deck's picture style, one per deck — the API's
 * `PICTURE_STYLES` (services/picture-style.ts; parity pinned in
 * __tests__/live-draft.spec.ts). The UI label for `photographic` is "Photo".
 * `abstract` draws nothing, the cover included.
 */
export const PICTURE_STYLES = [
  'illustrated',
  'photographic',
  'flat',
  'abstract',
] as const
export type PictureStyle = (typeof PICTURE_STYLES)[number]
export const DEFAULT_PICTURE_STYLE: PictureStyle = 'illustrated'

export function isPictureStyle(value: unknown): value is PictureStyle {
  return (
    typeof value === 'string' &&
    (PICTURE_STYLES as readonly string[]).includes(value)
  )
}

/** ListSessionsQueryDto mirror — `status` is a SINGLE value (IsIn, not array). */
export interface SessionListFilters {
  page?: number
  limit?: number
  search?: string
  status?: GenerationSessionStatus
}

// ── Normalizers (the hooks' `select` layer — apps/web CLAUDE.md rule 1) ────

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** Requires a numeric `seq`; everything else is defaulted. */
export function normalizeProgressEntry(
  raw: unknown,
): SessionProgressEntry | null {
  const rec = asRecord(raw)
  if (!rec || typeof rec.seq !== 'number' || !Number.isFinite(rec.seq)) {
    return null
  }
  const entry: SessionProgressEntry = {
    seq: rec.seq,
    stage: typeof rec.stage === 'string' ? rec.stage : 'unknown',
    message: typeof rec.message === 'string' ? rec.message : '',
    at: typeof rec.at === 'string' ? rec.at : '',
  }
  if (typeof rec.batchIndex === 'number') entry.batchIndex = rec.batchIndex
  if (typeof rec.totalBatches === 'number') {
    entry.totalBatches = rec.totalBatches
  }
  if (typeof rec.cardsDone === 'number') entry.cardsDone = rec.cardsDone
  if (typeof rec.cardsTotal === 'number') entry.cardsTotal = rec.cardsTotal
  return entry
}

/** Accepts a single entry or an array (the SSE relay may batch replays). */
export function coerceProgressEntries(raw: unknown): SessionProgressEntry[] {
  const list = Array.isArray(raw) ? raw : [raw]
  const out: SessionProgressEntry[] = []
  for (const item of list) {
    const entry = normalizeProgressEntry(item)
    if (entry) out.push(entry)
  }
  return out
}

/**
 * Union of two entry lists, deduped by `seq` (later list wins), ascending.
 * Used both to fold SSE deltas into local state and to merge the session
 * row's persisted `progress.entries` with live entries.
 */
export function mergeProgressEntries(
  base: SessionProgressEntry[],
  incoming: SessionProgressEntry[],
): SessionProgressEntry[] {
  const bySeq = new Map<number, SessionProgressEntry>()
  for (const entry of base) bySeq.set(entry.seq, entry)
  for (const entry of incoming) bySeq.set(entry.seq, entry)
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq)
}

export function normalizeCheckerResult(
  raw: unknown,
): SessionCheckerResult | null {
  const rec = asRecord(raw)
  if (!rec) return null
  return {
    accuracyOk: rec.accuracyOk === true,
    toneOk: rec.toneOk === true,
    safetyOk: rec.safetyOk === true,
    notes: Array.isArray(rec.notes)
      ? rec.notes.filter((n): n is string => typeof n === 'string')
      : [],
  }
}

function normalizeProgressState(raw: unknown): SessionProgressState {
  const rec = asRecord(raw)
  const entries = coerceProgressEntries(
    rec && Array.isArray(rec.entries) ? rec.entries : [],
  ).sort((a, b) => a.seq - b.seq)
  const maxSeq = entries.length ? entries[entries.length - 1].seq : 0
  const cursor =
    rec && typeof rec.cursor === 'number' && Number.isFinite(rec.cursor)
      ? Math.max(rec.cursor, maxSeq)
      : maxSeq
  return {
    cursor,
    entries,
    stage:
      rec && typeof rec.stage === 'string' ? rec.stage.toLowerCase() : null,
  }
}

/** Seq-less `progress` payload → typed snapshot (null if unusable). */
export function normalizeJobSnapshot(raw: unknown): SessionJobSnapshot | null {
  const rec = asRecord(raw)
  if (!rec) return null
  return {
    status: typeof rec.status === 'string' ? rec.status : null,
    stage: typeof rec.stage === 'string' ? rec.stage.toLowerCase() : null,
    percent:
      typeof rec.progress === 'number' && Number.isFinite(rec.progress)
        ? Math.max(0, Math.min(100, rec.progress))
        : null,
    message:
      typeof rec.message === 'string' && rec.message ? rec.message : null,
  }
}

/** Dense summary row: nullables null (never undefined). `??` lives HERE. */
export function normalizeGenerationSessionSummary(
  raw: GenerationSessionSummary,
): GenerationSessionSummary {
  const rec = raw as unknown as Record<string, unknown>
  return {
    id: typeof rec.id === 'string' ? rec.id : '',
    prompt: typeof rec.prompt === 'string' ? rec.prompt : '',
    status: isGenerationSessionStatus(rec.status) ? rec.status : 'In Progress',
    deckId: typeof rec.deckId === 'string' && rec.deckId ? rec.deckId : null,
    errorCode:
      typeof rec.errorCode === 'string' && rec.errorCode ? rec.errorCode : null,
    errorMessage:
      typeof rec.errorMessage === 'string' && rec.errorMessage
        ? rec.errorMessage
        : null,
    lastChunkAt: typeof rec.lastChunkAt === 'string' ? rec.lastChunkAt : null,
    createdAt: typeof rec.createdAt === 'string' ? rec.createdAt : '',
    updatedAt: typeof rec.updatedAt === 'string' ? rec.updatedAt : '',
  }
}

/**
 * The stored parameters, dense: unreadable members become null (or the
 * platform value for quality / picture style). A row without them (started
 * before they were stored) stays null.
 */
export function normalizeGenerationParams(
  raw: unknown,
): SessionGenerationParams | null {
  const rec = asRecord(raw)
  if (!rec) return null
  const text = (value: unknown) =>
    typeof value === 'string' && value ? value : null
  return {
    audience: text(rec.audience),
    locale: text(rec.locale),
    targetCardCount:
      typeof rec.targetCardCount === 'number' &&
      Number.isFinite(rec.targetCardCount)
        ? rec.targetCardCount
        : null,
    paletteKey: text(rec.paletteKey),
    backdrop: text(rec.backdrop),
    quality: (GENERATION_QUALITIES as readonly unknown[]).includes(rec.quality)
      ? (rec.quality as GenerationQuality)
      : 'standard',
    pictureStyle: isPictureStyle(rec.pictureStyle)
      ? rec.pictureStyle
      : DEFAULT_PICTURE_STYLE,
  }
}

/**
 * Dense session detail for components: arrays always arrays, nullables
 * null, progress always `{cursor, entries}`.
 */
export function normalizeGenerationSession(
  raw: GenerationSession,
): GenerationSession {
  const rec = raw as unknown as Record<string, unknown>
  return {
    ...normalizeGenerationSessionSummary(raw),
    outlineJson: asRecord(rec.outlineJson),
    deckJson: asRecord(rec.deckJson),
    checkerJson: normalizeCheckerResult(rec.checkerJson),
    progress: normalizeProgressState(rec.progress),
    generationParams: normalizeGenerationParams(rec.generationParams),
  }
}

/**
 * POST :id/save response → created deck id. Tolerant of the three plausible
 * BE shapes (the sessions lane is landing concurrently): `{deckId}`, the
 * updated session (`deckId` stamped), or the created deck row itself
 * (deck-shaped `{id, deckJson|cardCount, ...}`). A session object's own `id`
 * is NEVER mistaken for a deck id.
 */
export function extractSavedDeckId(resp: unknown): string | null {
  const rec = asRecord(resp)
  if (!rec) return null
  if (typeof rec.deckId === 'string' && rec.deckId) return rec.deckId
  const looksLikeDeck =
    typeof rec.id === 'string' &&
    rec.id.length > 0 &&
    // deck rows carry content fields a session row does not
    !('prompt' in rec) &&
    ('deckJson' in rec || 'cardCount' in rec)
  return looksLikeDeck ? (rec.id as string) : null
}
